import React from "react";
import { observer } from "mobx-react-lite";
import {
    Box,
    Button,
    Card,
    CardContent,
    Typography,
    Paper,
    Grid,
    Divider,
    MenuItem,
} from "@mui/material";
import CartStore from "../../stores/CartStore";
import InputField from "../form/InputField";
import toast from "../../utils/toast";
import { CartItem } from "../../models/Cart";

type Props = {
    cartStore: CartStore | null;
    customFieldValues: Record<string, Record<string, string>>;
    setCustomFieldValues: React.Dispatch<
        React.SetStateAction<Record<string, Record<string, string>>>
    >;
    onComplete: () => void;
    onBack: () => void;
};

type CustomFieldValues = Record<string, Record<string, string>>;

const TournamentCustomFields = observer(
    ({
        cartStore,
        customFieldValues,
        setCustomFieldValues,
        onComplete,
        onBack,
    }: Props) => {
        const [errors, setErrors] = React.useState<Record<string, string>>({});

        const tournamentItems = cartStore?.itemsWithCustomFields || [];

        React.useEffect(() => {
            // Initialize custom field values if not already set
            if (
                Object.keys(customFieldValues).length === 0 &&
                tournamentItems.length > 0
            ) {
                const initialValues: CustomFieldValues = {};

                tournamentItems.forEach((item) => {
                    const ticketKey = `ticket-${item.ticketId}`;
                    initialValues[ticketKey] = {};

                    item.department.customFields.forEach((field) => {
                        initialValues[ticketKey][field.internalName] = "";
                    });
                });

                setCustomFieldValues(initialValues);
            }
        }, [tournamentItems, customFieldValues, setCustomFieldValues]);

        const handleFieldChange = (
            ticketId: number,
            fieldName: string,
            value: any,
        ) => {
            const ticketKey = `ticket-${ticketId}`;
            setCustomFieldValues((prev) => ({
                ...prev,
                [ticketKey]: {
                    ...prev[ticketKey],
                    [fieldName]: value,
                },
            }));

            // Clear error for this field
            const errorKey = `${ticketKey}-${fieldName}`;
            if (errors[errorKey]) {
                setErrors((prev) => {
                    const newErrors = { ...prev };
                    delete newErrors[errorKey];
                    return newErrors;
                });
            }
        };

        const validateFields = (): boolean => {
            const newErrors: Record<string, string> = {};
            let isValid = true;

            tournamentItems.forEach((item) => {
                const ticketKey = `ticket-${item.ticketId}`;

                item.department.customFields.forEach((field) => {
                    if (field.required) {
                        const value =
                            customFieldValues[ticketKey]?.[
                                field.internalName
                            ] || "";
                        if (!value) {
                            const errorKey = `${ticketKey}-${field.internalName}`;
                            newErrors[errorKey] = `${field.name} is required`;
                            isValid = false;
                        }
                    }
                });
            });

            setErrors(newErrors);
            return isValid;
        };

        const handleContinue = () => {
            if (!validateFields()) {
                toast.error("Please fill in all required fields.");
                return;
            }

            onComplete();
        };

        const renderCustomFieldsForItem = (item: CartItem) => {
            const ticketKey = `ticket-${item.ticketId}`;

            return (
                <Card key={item.ticketId} sx={{ mb: 3 }}>
                    <CardContent>
                        <Typography variant="h6" gutterBottom color="primary">
                            {item.department.name}
                        </Typography>
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mb: 3 }}
                        >
                            {item.event.title}
                        </Typography>

                        <Grid container spacing={3}>
                            {item.department.customFields.map((field) => {
                                const errorKey = `${ticketKey}-${field.internalName}`;
                                const hasError = !!errors[errorKey];

                                return (
                                    <Grid item xs={12} md={6} key={field.id}>
                                        <InputField
                                            label={field.name}
                                            value={
                                                customFieldValues[ticketKey]?.[
                                                    field.internalName
                                                ] || ""
                                            }
                                            onChange={(e) => {
                                                handleFieldChange(
                                                    item.ticketId,
                                                    field.internalName,
                                                    field.type === "SELECT_BOX"
                                                        ? [
                                                              parseInt(
                                                                  e.target
                                                                      .value,
                                                                  10,
                                                              ),
                                                          ]
                                                        : e.target.value,
                                                );
                                            }}
                                            required={field.required}
                                            fullWidth
                                            error={hasError}
                                            helperText={
                                                hasError ? errors[errorKey] : ""
                                            }
                                            select={field.type === "SELECT_BOX"}
                                        >
                                            {field.type === "SELECT_BOX" && (
                                                <MenuItem value="">
                                                    <em>Select an option</em>
                                                </MenuItem>
                                            )}
                                            {field.options?.map(
                                                (option: any) => (
                                                    <MenuItem
                                                        key={option.id}
                                                        value={option.id}
                                                    >
                                                        {option.label}
                                                    </MenuItem>
                                                ),
                                            )}
                                        </InputField>
                                    </Grid>
                                );
                            })}
                        </Grid>
                    </CardContent>
                </Card>
            );
        };

        if (tournamentItems.length === 0) {
            return (
                <Box>
                    <Paper sx={{ p: 3, mb: 4 }}>
                        <Typography
                            variant="h4"
                            gutterBottom
                            textAlign="center"
                        >
                            Registration Details
                        </Typography>
                        <Typography
                            variant="body1"
                            textAlign="center"
                            color="text.secondary"
                        >
                            No additional registration details required.
                        </Typography>
                    </Paper>

                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            gap: 2,
                        }}
                    >
                        <Button variant="outlined" onClick={onBack}>
                            Back
                        </Button>
                        <Button
                            variant="contained"
                            onClick={onComplete}
                            size="large"
                        >
                            Continue to Payment
                        </Button>
                    </Box>
                </Box>
            );
        }

        return (
            <Box>
                <Paper sx={{ p: 3, mb: 4 }}>
                    <Typography variant="h4" gutterBottom textAlign="center">
                        Registration Details
                    </Typography>
                    <Typography
                        variant="body1"
                        textAlign="center"
                        color="text.secondary"
                    >
                        Please provide the required information for each
                        tournament registration.
                    </Typography>
                </Paper>

                {tournamentItems.map(renderCustomFieldsForItem)}

                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        gap: 2,
                        mt: 4,
                    }}
                >
                    <Button variant="outlined" onClick={onBack}>
                        Back
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleContinue}
                        size="large"
                    >
                        Continue to Payment
                    </Button>
                </Box>
            </Box>
        );
    },
);

export default TournamentCustomFields;
